import { nextTick, onBeforeUnmount, onMounted, readonly, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useSeasonalCornerBottomInsetPx } from '~/composables/ui/useSeasonalCornerBottomInsetPx'
import { APP_CORNER_BOTTOM_MARGIN_PX } from '~/composables/ui/useSourceToast'

/** Espace entre le bas de la toolbox / toasts et le bord haut du footer quand le footer remonte sous le coin fixe. */
const APP_FIXED_CORNER_FOOTER_GAP_PX = 8

const APP_LAYOUT_FOOTER_SELECTOR = '[data-monorg-app-footer]'

const sharedExtraBottomPx = ref(0)

let subscriberCount = 0
let scheduledFrameId = 0
let footerResizeObserver: ResizeObserver | null = null

function resolveAppLayoutFooter(): HTMLElement | null {
  if (!import.meta.client) {
    return null
  }
  const found = document.querySelector(APP_LAYOUT_FOOTER_SELECTOR)
  if (found instanceof HTMLElement) {
    return found
  }
  return null
}

function bindFooterResizeObserver(): void {
  footerResizeObserver?.disconnect()
  footerResizeObserver = null
  const footer = resolveAppLayoutFooter()
  if (!footer) {
    return
  }
  footerResizeObserver = new ResizeObserver(() => {
    scheduleAppLayoutFooterLiftRecompute(lastSeasonalInset)
  })
  footerResizeObserver.observe(footer)
}

function recomputeAppLayoutFooterLiftPx(seasonalInsetPx: number): void {
  if (!import.meta.client) {
    sharedExtraBottomPx.value = 0
    return
  }
  const footer = resolveAppLayoutFooter()
  if (!footer) {
    sharedExtraBottomPx.value = 0
    return
  }
  const footerRect = footer.getBoundingClientRect()
  const innerHeight = window.innerHeight
  const baseBottom = APP_CORNER_BOTTOM_MARGIN_PX + seasonalInsetPx
  const requiredBottom = innerHeight - footerRect.top + APP_FIXED_CORNER_FOOTER_GAP_PX
  const totalBottom = Math.max(baseBottom, requiredBottom)
  const derivedExtra = Math.round(totalBottom - baseBottom)
  sharedExtraBottomPx.value = Math.max(0, derivedExtra)
}

function scheduleAppLayoutFooterLiftRecompute(seasonalInsetPx: number): void {
  if (!import.meta.client) {
    return
  }
  if (scheduledFrameId !== 0) {
    return
  }
  scheduledFrameId = window.requestAnimationFrame(() => {
    scheduledFrameId = 0
    recomputeAppLayoutFooterLiftPx(seasonalInsetPx)
  })
}

function teardownAppLayoutFooterLiftListeners(): void {
  footerResizeObserver?.disconnect()
  footerResizeObserver = null
  if (scheduledFrameId !== 0) {
    window.cancelAnimationFrame(scheduledFrameId)
    scheduledFrameId = 0
  }
  if (!import.meta.client) {
    return
  }
  window.removeEventListener('scroll', scrollListener, true)
  window.removeEventListener('resize', resizeListener)
}

let lastSeasonalInset = 0

function scrollListener(): void {
  scheduleAppLayoutFooterLiftRecompute(lastSeasonalInset)
}

function resizeListener(): void {
  bindFooterResizeObserver()
  scheduleAppLayoutFooterLiftRecompute(lastSeasonalInset)
}

function setupAppLayoutFooterLiftListeners(): void {
  if (!import.meta.client) {
    return
  }
  window.addEventListener('scroll', scrollListener, { passive: true, capture: true })
  window.addEventListener('resize', resizeListener)
  bindFooterResizeObserver()
  scheduleAppLayoutFooterLiftRecompute(lastSeasonalInset)
}

/**
 * Décalage supplémentaire (px) sur `bottom` des éléments fixes bas-droite pour qu’ils ne chevauchent pas
 * le footer du layout principal au scroll.
 */
export function useAppLayoutFooterFixedBottomLiftPx() {
  const route = useRoute()
  const seasonalCornerBottomInsetPx = useSeasonalCornerBottomInsetPx()

  function syncSeasonalAndSchedule(): void {
    lastSeasonalInset = seasonalCornerBottomInsetPx.value
    scheduleAppLayoutFooterLiftRecompute(lastSeasonalInset)
  }

  watch(seasonalCornerBottomInsetPx, () => {
    syncSeasonalAndSchedule()
  })

  watch(
    () => route.fullPath,
    () => {
      void nextTick(() => {
        bindFooterResizeObserver()
        syncSeasonalAndSchedule()
      })
    },
  )

  onMounted(() => {
    if (!import.meta.client) {
      return
    }
    lastSeasonalInset = seasonalCornerBottomInsetPx.value
    subscriberCount += 1
    if (subscriberCount === 1) {
      setupAppLayoutFooterLiftListeners()
    } else {
      bindFooterResizeObserver()
      scheduleAppLayoutFooterLiftRecompute(lastSeasonalInset)
    }
    void nextTick(() => {
      bindFooterResizeObserver()
      syncSeasonalAndSchedule()
    })
  })

  onBeforeUnmount(() => {
    if (!import.meta.client) {
      return
    }
    subscriberCount -= 1
    if (subscriberCount <= 0) {
      subscriberCount = 0
      teardownAppLayoutFooterLiftListeners()
      sharedExtraBottomPx.value = 0
    }
  })

  return {
    extraBottomPx: readonly(sharedExtraBottomPx),
  }
}
